import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Stack,
  Tooltip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Savings, Refresh, History, OpenInNew, InfoOutlined, TaskAlt, ForwardToInbox } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getDepositsSummary,
  getDepositsReport,
  getDepositAuditTrail,
  closeDeposit,
  notifyDepositByEmail,
  DepositSummaryResponse,
  DepositReportRow,
  DepositAuditEntry,
} from '../../../services/request/Deposits';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { toast } from 'react-toastify';

interface PageResponse<T> {
  content?: T[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
}

const CustomerDepositsMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DepositSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [rows, setRows] = useState<DepositReportRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [reportLoading, setReportLoading] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState<DepositAuditEntry[]>([]);
  const [auditLabel, setAuditLabel] = useState('');
  const [closeOpen, setCloseOpen] = useState(false);
  const [rowToClose, setRowToClose] = useState<DepositReportRow | null>(null);
  const [closeForm, setCloseForm] = useState({ paymentMode: 'BANK_TRANSFER', comment: '' });
  const [closing, setClosing] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [rowToNotify, setRowToNotify] = useState<DepositReportRow | null>(null);
  const [notifyForm, setNotifyForm] = useState({ email: '', message: '' });
  const [sendingNotify, setSendingNotify] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const res = await getDepositsSummary();
      setSummary((res?.data as DepositSummaryResponse) ?? null);
    } catch {
      toast.error('Failed to load deposit summary');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadReport = useCallback(async () => {
    try {
      setReportLoading(true);
      const res = await getDepositsReport(page, rowsPerPage);
      const body = res?.data as PageResponse<DepositReportRow> | undefined;
      const content = body?.content ?? [];
      setRows(Array.isArray(content) ? content : []);
      setTotalRows(typeof body?.totalElements === 'number' ? body.totalElements : content.length);
    } catch {
      toast.error('Failed to load deposit report');
      setRows([]);
      setTotalRows(0);
    } finally {
      setReportLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleRefreshAll = () => {
    loadSummary();
    loadReport();
  };

  const formatDepositDate = (d: string | undefined) => {
    if (!d) return '—';
    if (typeof d === 'string' && d.includes('T')) return new Date(d).toLocaleDateString('en-IN');
    return d;
  };

  const formatCreated = (d: string | undefined) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-IN');
    } catch {
      return d;
    }
  };

  const openCloseDialog = (row: DepositReportRow) => {
    setRowToClose(row);
    setCloseForm({ paymentMode: 'BANK_TRANSFER', comment: '' });
    setCloseOpen(true);
  };

  const handleSubmitClose = async () => {
    if (!rowToClose) return;
    const c = closeForm.comment.trim();
    if (!c) {
      toast.error('Comment is required');
      return;
    }
    try {
      setClosing(true);
      await closeDeposit(rowToClose.depositId, { comment: c, paymentMode: closeForm.paymentMode });
      toast.success('Deposit closed');
      setCloseOpen(false);
      setRowToClose(null);
      loadSummary();
      loadReport();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to close';
      toast.error(typeof msg === 'string' ? msg : 'Failed to close');
    } finally {
      setClosing(false);
    }
  };

  const openNotifyDialog = (row: DepositReportRow) => {
    setRowToNotify(row);
    setNotifyForm({ email: '', message: '' });
    setNotifyOpen(true);
  };

  const handleSendNotify = async () => {
    if (!rowToNotify) return;
    const em = notifyForm.email.trim();
    if (!em) {
      toast.error('Enter recipient email');
      return;
    }
    try {
      setSendingNotify(true);
      await notifyDepositByEmail(rowToNotify.depositId, {
        recipientEmail: em,
        message: notifyForm.message.trim() || undefined,
      });
      toast.success('Deposit notification sent');
      setNotifyOpen(false);
      setRowToNotify(null);
      loadReport();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to send';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send');
    } finally {
      setSendingNotify(false);
    }
  };

  const openAudit = async (row: DepositReportRow) => {
    setAuditLabel(
      `${row.borrowerName || row.borrowerId} · ${formatCurrency(row.amount)} · ${formatDepositDate(row.depositDate as string)}`
    );
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const res = await getDepositAuditTrail(row.depositId);
      const list = res?.data ?? [];
      setAuditEntries(Array.isArray(list) ? list : []);
    } catch {
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const statusChip = (status?: string) => {
    const s = (status || '').toUpperCase();
    const color =
      s === 'ACTIVE' ? 'success' : s === 'REVERSED' || s === 'CANCELLED' ? 'default' : 'warning';
    return <Chip size="small" label={status || '—'} color={color} variant={s === 'ACTIVE' ? 'filled' : 'outlined'} />;
  };

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Customer deposits — monitoring & compliance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Portfolio summary, full deposit register, and per-deposit audit trail for reporting and tracing.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefreshAll}>
          Refresh
        </Button>
      </Stack>

      <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3 }}>
        Changes to deposits are recorded in the audit log (who, when, action, before/after where applicable). Use{' '}
        <strong>Audit</strong> on any row for the full trail. Record new deposits from{' '}
        <strong>Customers</strong> or a customer&apos;s detail page.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Savings color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total active deposits (INR)
                </Typography>
              </Stack>
              {summaryLoading ? (
                <LinearProgress />
              ) : (
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(summary?.totalActiveAmount ?? 0)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Active deposit records
              </Typography>
              {summaryLoading ? (
                <LinearProgress />
              ) : (
                <Typography variant="h5" fontWeight={700}>
                  {summary?.activeDepositCount ?? 0}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Deposit register (paginated)
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Maturity</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Closure</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportLoading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography color="text.secondary">No deposits found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.depositId} hover>
                      <TableCell>{formatDepositDate(row.depositDate as string)}</TableCell>
                      <TableCell>{row.maturityDate ? formatDepositDate(row.maturityDate as string) : '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.borrowerName || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          ID: {row.borrowerId}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatCurrency(row.amount)}</TableCell>
                      <TableCell>{row.source || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={row.referenceNumber || ''}>
                          <span>{row.referenceNumber || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{statusChip(row.status)}</TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        {row.status === 'CLOSED' ? (
                          <Box>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {row.closurePaymentMode || '—'}
                            </Typography>
                            <Tooltip title={row.closureComment || ''}>
                              <Typography variant="caption" noWrap display="block">
                                {row.closureComment || '—'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {row.createdBy || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCreated(row.createdDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Open customer">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/customers/${row.borrowerId}`)}
                            aria-label="Open customer"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Email deposit details">
                          <IconButton size="small" onClick={() => openNotifyDialog(row)} color="primary" aria-label="Notify email">
                            <ForwardToInbox fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.status === 'ACTIVE' && (
                          <Tooltip title="Close deposit">
                            <IconButton
                              size="small"
                              onClick={() => openCloseDialog(row)}
                              aria-label="Close deposit"
                              color="warning"
                            >
                              <TaskAlt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Audit trail">
                          <IconButton size="small" onClick={() => openAudit(row)} aria-label="Audit trail">
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>

      <Dialog open={notifyOpen} onClose={() => !sendingNotify && setNotifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notify about deposit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Enter any email address. Optional message is included in the email. Logged in audit trail.
          </Typography>
          {rowToNotify && (
            <Typography variant="subtitle2" gutterBottom>
              {rowToNotify.borrowerName || rowToNotify.borrowerId} · {formatCurrency(rowToNotify.amount)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Recipient email"
            type="email"
            value={notifyForm.email}
            onChange={(e) => setNotifyForm({ ...notifyForm, email: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Custom message (optional)"
            multiline
            minRows={3}
            value={notifyForm.message}
            onChange={(e) => setNotifyForm({ ...notifyForm, message: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)} disabled={sendingNotify}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<ForwardToInbox />} onClick={handleSendNotify} disabled={sendingNotify}>
            {sendingNotify ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeOpen} onClose={() => !closing && setCloseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close deposit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Settlement payment mode and a required comment are stored on the deposit and in the audit log.
          </Typography>
          {rowToClose && (
            <Typography variant="subtitle2" gutterBottom>
              {rowToClose.borrowerName || rowToClose.borrowerId} · {formatCurrency(rowToClose.amount)}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Settlement payment mode</InputLabel>
            <Select
              value={closeForm.paymentMode}
              label="Settlement payment mode"
              onChange={(e) => setCloseForm({ ...closeForm, paymentMode: e.target.value })}
            >
              <MenuItem value="BANK_TRANSFER">Bank transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Comment (required)"
            multiline
            minRows={3}
            value={closeForm.comment}
            onChange={(e) => setCloseForm({ ...closeForm, comment: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)} disabled={closing}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleSubmitClose} disabled={closing}>
            {closing ? 'Closing…' : 'Close deposit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit trail — {auditLabel}</DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : auditEntries.length === 0 ? (
            <Typography color="text.secondary">No audit entries.</Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Delta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditEntries.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      {a.performedAt ? new Date(a.performedAt).toLocaleString('en-IN') : '—'}
                    </TableCell>
                    <TableCell>{a.action}</TableCell>
                    <TableCell>{a.summary || '—'}</TableCell>
                    <TableCell>{a.performedBy || '—'}</TableCell>
                    <TableCell>
                      {a.previousAmount != null && a.newAmount != null ? (
                        <Typography variant="caption">
                          {formatCurrency(a.previousAmount)} → {formatCurrency(a.newAmount)}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDepositsMonitoring;
